import * as React from 'react';

import {Field, Fields, FieldArray} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import type {ActivityType} from 'nutshell-core/types';

import {Label} from 'shells/form/label';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {SidebarHeader} from 'shells/sidebar/sidebar-header';

import type {Comparator, GoalType} from 'nutshell-graphql-types';
import type {SalesAutomationStageGoal as SalesAutomationStageGoalFragment} from 'nutshell-graphql-types';

import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {ReduxFormFormattedNumberField} from '../fields/redux-form-formatted-number-field';

import {FieldActivityTypes} from './field-activity-types';
import {FieldLeadFields} from './field-lead-fields';
import {FieldsBasicInfo} from './fields-basic-info';
import {GoalTypeSelect} from './goal-type-select';

import styles from '../sales-process-form.module.css';

const LEAD_VALUE = 'LEAD_VALUE';
const BASIC_INFO = 'BASIC_INFO';
const ACTIVITY_LOGGED = 'ACTIVITY_LOGGED';
const ACTIVITY_SCHEDULED = 'ACTIVITY_SCHEDULED';
const LEAD_FIELDS = 'LEAD_FIELDS';

export type GoalsFormData = {
    scheduledActivityTypes: Array<string | null | undefined>;
    loggedActivityTypes: Array<string | null | undefined>;
    goalType: GoalType | null | undefined;
    hasRelated: {
        name?: boolean;
        emailAddress?: boolean;
        phone?: boolean;
    };
    currencyValue?: number;
    currencyComparator?: Comparator;
    // This is essentially a ConditionConfigEntityFilter[] without
    // typenames. See `graphql-types.js`. Couldnt' figure out how to
    // type this without a mega restructuring of this system.
    leadFields: Array<any | null | undefined>;
};

type Props = {
    isFetchingLatestVersion: boolean;
    goal: SalesAutomationStageGoalFragment | null | undefined;
    goalTypeSelected: GoalType | null | undefined;
    scheduledActivityTypes: ActivityType[];
    loggedActivityTypes: ActivityType[];
    error?: string;
    isSubmitting: boolean;
    onDelete: (entityId: string) => Promise<any>;
    onClose: () => void;
    handleSubmit: FormProps['handleSubmit'];
};

type State = {
    goalSelectHeight: number;
};

export class FormGoalsComponent extends React.Component<Props, State> {
    inputWrapper: HTMLDivElement | null | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            goalSelectHeight: 300,
        };
        this.inputWrapper = null;
    }

    componentDidMount() {
        this.updateGoalSelectHeight();
    }

    componentDidUpdate() {
        this.updateGoalSelectHeight();
    }

    render() {
        const id = this.props.goal ? this.props.goal.id : null;
        // We only want to show a loading spinner if we are fetching, but don't
        // know the id yet.  This happens on the first load of a stage's goal,
        // but not on subsequent loads and not for new goal creation.
        const showLoading = this.props.isFetchingLatestVersion && !id;

        return (
            <React.Fragment>
                <SidebarHeader
                    saveText={id ? 'Save' : 'Add'}
                    onSave={this.props.handleSubmit}
                    closeButton={true}
                    submitButton={true}
                    submitDisabled={this.props.isSubmitting}
                    isSaving={this.props.isSubmitting}
                    onClose={this.props.onClose}
                    headerTitle={id ? 'Edit stage goal' : 'What is your stage goal?'}
                    subtitle={this.props.goal ? `within ${this.props.goal.stage.name}` : ''}
                />
                {showLoading ? (
                    <div className={styles['loading-container']}>
                        <LoadingIcon />
                    </div>
                ) : (
                    <SalesAutomationSidebarFormWrapper
                        id={id}
                        isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                        onDelete={{
                            deleteText: 'Remove goal',
                            deleteConfirmationTitle: 'Are you sure you want to remove this goal?',
                            onDeleteConfirm: this.handleDeleteConfirm,
                        }}
                        onSaveForm={this.props.handleSubmit}
                        errorMessage={this.props.error}
                    >
                        <p className={styles.description}>
                            Nutshell watches each lead in this stage, and automatically advances it
                            when your goal has been met.
                        </p>

                        <div className={styles['form-input-goal-type-select']}>
                            <Label htmlFor='goalType'>What is your goal for this stage?</Label>
                            <Field
                                name='goalType'
                                id='goalType'
                                component={GoalTypeSelect}
                                maxMenuHeight={this.state.goalSelectHeight}
                            />
                            <div
                                ref={(ref) => (this.inputWrapper = ref)}
                                className={styles['input-wrapper']}
                            >
                                {this.renderGoalForm()}
                            </div>
                        </div>
                    </SalesAutomationSidebarFormWrapper>
                )}
            </React.Fragment>
        );
    }

    updateGoalSelectHeight = () => {
        if (this.inputWrapper) {
            const newHeight = Math.max(this.inputWrapper.offsetHeight, 210);
            if (this.state.goalSelectHeight !== newHeight) {
                this.setState({goalSelectHeight: newHeight});
            }
        }
    };

    renderGoalForm = () => {
        switch (this.props.goalTypeSelected) {
            case BASIC_INFO:
                return (
                    <div>
                        <Label htmlFor='currencyValue'>Advance the lead after entering…</Label>
                        <Fields
                            names={[
                                'hasRelated',
                                'hasRelated.name',
                                'hasRelated.emailAddress',
                                'hasRelated.phone',
                            ]}
                            component={FieldsBasicInfo}
                        />
                    </div>
                );
            case LEAD_VALUE:
                return (
                    <div className={styles['form-input']}>
                        <Label htmlFor='currencyValue'>Advance if the lead is greater than…</Label>
                        <Field
                            id='currencyValue'
                            name='currencyValue'
                            component={ReduxFormFormattedNumberField}
                        />
                    </div>
                );
            case ACTIVITY_LOGGED:
                return (
                    <div>
                        <Label htmlFor='loggedActivityTypes'>Advance the lead after a…</Label>
                        <FieldArray
                            name='loggedActivityTypes'
                            rerenderOnEveryChange={true}
                            activityTypes={this.props.loggedActivityTypes}
                            bottomRowText='is logged'
                            component={FieldActivityTypes}
                        />
                    </div>
                );
            case ACTIVITY_SCHEDULED:
                return (
                    <div>
                        <Label htmlFor='scheduledActivityTypes'>Advance the lead after a…</Label>
                        <FieldArray
                            name='scheduledActivityTypes'
                            rerenderOnEveryChange={true}
                            activityTypes={this.props.scheduledActivityTypes}
                            bottomRowText='is scheduled'
                            component={FieldActivityTypes}
                        />
                    </div>
                );
            case LEAD_FIELDS:
                return (
                    <div>
                        <Label htmlFor='leadFields'>Advance the lead if…</Label>
                        <FieldArray
                            name='leadFields'
                            rerenderOnEveryChange={true}
                            component={FieldLeadFields}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    handleDeleteConfirm = (id: string) => {
        return this.props.onDelete(id);
    };
}
